import {
  AddRounded as AddRoundedIcon,
  Archive as ArchiveIcon,
  ArchiveOutlined as ArchiveOutlinedIcon,
  ArrowBackRounded as ArrowBackRoundedIcon,
  ArrowDownwardRounded as ArrowDownwardRoundedIcon,
  ArrowDropDown as ArrowDropDownIcon,
  ArrowDropUp as ArrowDropUpIcon,
  ArrowForwardRounded as ArrowForwardRoundedIcon,
  ArrowUpwardRounded as ArrowUpwardRoundedIcon,
  CheckCircleOutlineOutlined as CheckCircleOutlineOutlinedIcon,
  Check as CheckIcon,
  CheckOutlined as CheckOutlinedIcon,
  Close as CloseIcon,
  CloseRounded as CloseRoundedIcon,
  Code as CodeIcon,
  CommentOutlined as CommentOutlinedIcon,
  ContentCopy as ContentCopyIcon,
  DataObject as DataObjectIcon,
  Delete as DeleteIcon,
  DeleteOutline as DeleteOutlineIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  DragIndicator as DragIndicatorIcon,
  Edit as EditIcon,
  Email,
  EmailOutlined as EmailOutlinedIcon,
  ErrorOutlineRounded as ErrorOutlineRoundedIcon,
  FileUploadRounded as FileUploadRoundedIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  FormatLineSpacingRounded as FormatLineSpacingRoundedIcon,
  HighlightOff as HighlightOffIcon,
  HowToRegOutlined as HowToRegOutlinedIcon,
  Image as ImageIcon,
  InfoOutlined as InfoOutlinedIcon,
  InsertDriveFile as InsertDriveFileIcon,
  InsertDriveFileOutlined as InsertDriveFileOutlinedIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowLeftRounded as KeyboardArrowLeftRoundedIcon,
  KeyboardArrowRightRounded as KeyboardArrowRightRoundedIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  LibraryBooksOutlined as LibraryBooksOutlinedIcon,
  LockOpenOutlined as LockOpenOutlinedIcon,
  LockOutlined as LockOutlinedIcon,
  MediationRounded as MediationRoundedIcon,
  MoreVertRounded as MoreVertRoundedIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Refresh as RefreshIcon,
  Restore as RestoreIcon,
  SaveOutlined as SaveOutlinedIcon,
  SearchRounded as SearchRoundedIcon,
  Settings as SettingsIcon,
  Share as ShareIcon,
  SupervisorAccountRounded as SupervisorAccountRoundedIcon,
  SupportAgentRounded as SupportAgentRoundedIcon,
  SwapHorizOutlined as SwapHorizOutlinedIcon,
  SwapHorizRounded as SwapHorizRoundedIcon,
  TaskAltRounded as TaskAltRoundedIcon,
  Undo as UndoRounded,
  UploadFile as UploadFileIcon,
  VisibilityOffRounded as VisibilityOffRoundedIcon,
  VisibilityRounded as VisibilityRoundedIcon,
  WarningAmberRounded as WarningAmberRoundedIcon
} from '@mui/icons-material'
import {
  SvgIcon,
  type SvgIconProps,
  type SxProps,
  type Theme,
  useTheme
} from '@mui/material'

import { variants } from './icon-variants'

export const iconComponents = {
  CODE: CodeIcon,
  DATA_OBJECT: DataObjectIcon,
  DESCRIPTION: DescriptionIcon,
  DOWNLOAD: DownloadIcon,
  IMAGE_FILE: ImageIcon,
  INSERT_DRIVE_FILE: InsertDriveFileIcon,
  PICTURE_AS_PDF: PictureAsPdfIcon,
  UPLOAD_FILE: UploadFileIcon,
  ERROR_OUTLINE: ErrorOutlineRoundedIcon,
  CHECK_CIRCLE_OUTLINE: CheckCircleOutlineOutlinedIcon,
  CHECK_CIRCLE_TICK: TaskAltRoundedIcon,
  INFO_OUTLINE: InfoOutlinedIcon,
  WARNING: WarningAmberRoundedIcon,
  HIGHLIGHT_OFF: HighlightOffIcon,
  ARROW_UPWARD: ArrowUpwardRoundedIcon,
  ARROW_DOWNWARD: ArrowDownwardRoundedIcon,
  PLUS: AddRoundedIcon,
  TRASH_CONTAINED: DeleteIcon,
  TRASH_OUTLINED: DeleteOutlineIcon,
  EDIT_PENCIL: EditIcon,
  ARROW_LEFT: ArrowBackRoundedIcon,
  ARROW_RIGHT: ArrowForwardRoundedIcon,
  CHEVRON_LEFT: KeyboardArrowLeftRoundedIcon,
  CHEVRON_RIGHT: KeyboardArrowRightRoundedIcon,
  CHEVRON_UP: KeyboardArrowUpIcon,
  CHEVRON_DOWN: KeyboardArrowDownIcon,
  SEARCH_MAGNIFY: SearchRoundedIcon,
  CLOSE_X: CloseRoundedIcon,
  REMOVE_X: CloseIcon,
  SUPERVISOR: SupervisorAccountRoundedIcon,
  VISIBILITY_OFF: VisibilityOffRoundedIcon,
  VISIBILITY_ON: VisibilityRoundedIcon,
  FILE_UPLOAD: FileUploadRoundedIcon,
  FOLDER: FolderIcon,
  FOLDER_OPEN: FolderOpenIcon,
  SAVE: SaveOutlinedIcon,
  REFRESH: RefreshIcon,
  RESTORE: RestoreIcon,
  SETTINGS_WHEEL: SettingsIcon,
  ARCHIVE_FILLED: ArchiveIcon,
  ARCHIVE_OUTLINED: ArchiveOutlinedIcon,
  NOTES: CommentOutlinedIcon,
  REORDER: FormatLineSpacingRoundedIcon,
  LINK: MediationRoundedIcon,
  MORE_VERT: MoreVertRoundedIcon,
  EMAIL: EmailOutlinedIcon,
  EMAIL_FILLED: Email,
  APPROVAL: HowToRegOutlinedIcon,
  SUPPORT: SupportAgentRoundedIcon,
  DRAG_DROP: DragIndicatorIcon,
  CHECK: CheckIcon,
  ARROW_DROP_DOWN: ArrowDropDownIcon,
  ARROW_DROP_UP: ArrowDropUpIcon,
  DRAFT: InsertDriveFileOutlinedIcon,
  LOCK_OPEN: LockOpenOutlinedIcon,
  LOCK_CLOSED: LockOutlinedIcon,
  BOOK: LibraryBooksOutlinedIcon,
  SWAP: SwapHorizOutlinedIcon,
  SWAP_ROUNDED: SwapHorizRoundedIcon,
  COPY_ICON: ContentCopyIcon,
  CHECK_OUTLINE: CheckOutlinedIcon,
  UNDO: UndoRounded,
  SHARE: ShareIcon
} as const

export const customIconComponents = {
  ATTACHMENT: 'attachment-icon',
  EQUIPMENT: 'equipment-icon',
  LAPTOP_ICON: 'laptop-icon',
  BOLT_ICON: 'bolt-icon'
}

export type CustomIconVariant = keyof typeof customIconComponents

export type IconVariant = keyof typeof iconComponents

export type IconColor = 'success' | 'error' | 'warning' | 'info' | 'ghost'

export type MolsonIconProps = SvgIconProps & {
  variant?: IconVariant
  customVariant?: CustomIconVariant
  onClick?: React.MouseEventHandler
  dataTestId?: string
  customHeight?: string | number
  customWidth?: string | number
  iconColor?: IconColor
  sx?: SxProps<Theme>
}

export const MolsonIcon = ({
  variant,
  customVariant,
  onClick,
  dataTestId,
  customHeight,
  customWidth,
  iconColor,
  sx,
  ...props
}: MolsonIconProps) => {
  const theme = useTheme()

  const handleIconColor = () => {
    switch (iconColor) {
      case 'success': {
        return theme.palette.primary.success
      }
      case 'error': {
        return theme.palette.primary.error
      }
      case 'warning': {
        return theme.palette.secondary.warning
      }
      case 'info': {
        return theme.palette.secondary.blue
      }
      case 'ghost': {
        return theme.palette.secondary[500]
      }

      default:
        return theme.palette.secondary[900]
    }
  }

  const IconComponent = iconComponents[variant as IconVariant]

  const CustomIconComponent =
    customIconComponents[customVariant as CustomIconVariant]

  return customVariant ? (
    <SvgIcon
      viewBox={variants[CustomIconComponent].viewBox}
      fill="none"
      preserveAspectRatio="none"
      onClick={onClick}
      data-testid={dataTestId}
      {...props}
      sx={{
        width: customWidth ?? variants[CustomIconComponent]?.width,
        height: customHeight ?? variants[CustomIconComponent]?.height,
        color: handleIconColor(),
        ...sx
      }}
    >
      {variants[CustomIconComponent]?.path}
    </SvgIcon>
  ) : (
    <IconComponent
      onClick={onClick}
      data-testid={dataTestId}
      sx={{
        height: customHeight,
        width: customWidth,
        color: handleIconColor(),
        ...sx
      }}
      {...props}
    />
  )
}
